// @ts-nocheck
import React from 'react';
import PropTypes from 'react-proptypes';
import { Feature, Record } from '@fulcrumapp/fulcrum-core';
import MetadataTitle from './metadata-title';
import Collapse from '../../shared/collapse';
import ProjectField from './project-field';
import AssignmentField from './assignment-field';
import StatusField from './status-field';
import TimestampField from './timestamp-field';
import LocationField from './location-field';
import GeometryField from './geometry-field';
import ChangesetField from './changeset-field';
import DurationField from './duration-field';
import AuditLocationField from './audit-location-field';
import { FormAppContext } from '../../hooks/use-form-app';

export default class MetadataView extends React.Component {
  static propTypes = {
    editing: PropTypes.bool.isRequired,
    addMode: PropTypes.bool.isRequired,
    record: PropTypes.instanceOf(Record).isRequired,
    feature: PropTypes.instanceOf(Feature).isRequired,
    // visible: PropTypes.bool.isRequired,
    onProjectChange: PropTypes.func.isRequired,
    onAssignmentChange: PropTypes.func.isRequired,
    onStatusChange: PropTypes.func.isRequired,
    onGeometryChange: PropTypes.func.isRequired,
  };

  static contextType = FormAppContext;

  static defaultPropTypes = {
    visible: true,
  };

  constructor(props, context) {
    super(props, context);

    this.state = {
      expanded: this.context.preferences.isSectionExpanded('metadata'),
    };
  }

  render() {
    return this.renderEditable();
  }

  renderEditable() {
    const { styles } = MetadataView;

    const container = css(styles.container, styles[this.featureType]);

    return (
      <div className={container}>
        <MetadataTitle
          onClick={this.handleTitleClick}
          expanded={this.state.expanded}
        />
        <Collapse open={this.state.expanded}>
          <div className={styles.fields}>
            <TimestampField
              show={!this.props.addMode && this.isRecord}
              feature={this.props.feature}
              record={this.props.record}
              editing={this.props.editing}
              field="clientCreatedAt"
              label="Created"
              part="device"
            />

            <TimestampField
              show={!this.props.addMode && this.isRecord}
              feature={this.props.feature}
              record={this.props.record}
              editing={this.props.editing}
              field="clientUpdatedAt"
              label="Updated"
              part="device"
            />

            <TimestampField
              show={!this.props.addMode}
              feature={this.props.feature}
              record={this.props.record}
              editing={this.props.editing}
              field="createdAt"
              label="Created"
              part={this.isRecord ? 'web' : 'device'}
            />

            <TimestampField
              show={!this.props.addMode}
              feature={this.props.feature}
              record={this.props.record}
              editing={this.props.editing}
              field="updatedAt"
              label="Updated"
              part={this.isRecord ? 'web' : 'device'}
            />

            {this.renderDurationField()}

            {this.renderChangesetField()}

            {this.renderLocationField()}

            {this.renderGeometryField()}

            {this.renderCreatedLocationField()}

            {this.renderUpdatedLocationField()}

            {this.renderStatusField()}

            {this.renderProjectField()}

            {this.renderAssignmentField()}
          </div>
        </Collapse>
        <div className={css(styles.spacer, this.state.expanded && styles.spacerExpanded)} />
      </div>
    );
  }

  renderDurationField() {
    if (this.props.feature.createdDuration == null
        && this.props.feature.updatedDuration == null
        && this.props.feature.editedDuration == null) {
      return null;
    }

    return (
      <DurationField
        feature={this.props.feature}
        record={this.props.record}
        editing={this.props.editing}
        field="durations"
      />
    );
  }

  renderLocationField() {
    const {
      feature, onGeometryChange, editing, record: { form },
    } = this.props;
    const { element } = feature;
    const { isManualLocationEnabled } = form;
    const enabled = this.isRecord ? form.isGeometryEnabled : element.isGeometryEnabled;

    if (enabled) {
      return (
        <LocationField
          feature={feature}
          isManualLocationEnabled={isManualLocationEnabled}
          editing={editing}
          field="location"
          onGeometryChange={onGeometryChange}
        />
      );
    }

    return null;
  }

  renderGeometryField() {
    const {
      feature, onGeometryChange, editing,
    } = this.props;

    const showGeometryField = !!feature.geometry && feature?.geometry?.type !== 'Point';

    if (showGeometryField) {
      return (
        <GeometryField
          feature={feature}
          editing={editing}
          field="geometry"
          onGeometryChange={onGeometryChange}
        />
      );
    }

    return null;
  }

  renderCreatedLocationField() {
    const enabled = this.props.feature.hasCreatedCoordinate;

    if (enabled) {
      return (
        <AuditLocationField
          feature={this.props.feature}
          record={this.props.record}
          editing={this.props.editing}
          latitude={this.props.feature.createdLatitude}
          longitude={this.props.feature.createdLongitude}
          altitude={this.props.feature.createdAltitude}
          accuracy={this.props.feature.createdAccuracy}
          key="created_location"
          field="created_location"
          locationTitle="Created Location"
        />
      );
    }

    return null;
  }

  renderUpdatedLocationField() {
    const enabled = this.props.feature.hasUpdatedCoordinate;

    if (enabled) {
      return (
        <AuditLocationField
          feature={this.props.feature}
          record={this.props.record}
          editing={this.props.editing}
          latitude={this.props.feature.updatedLatitude}
          longitude={this.props.feature.updatedLongitude}
          altitude={this.props.feature.updatedAltitude}
          accuracy={this.props.feature.updatedAccuracy}
          key="updated_location"
          field="updated_location"
          locationTitle="Updated Location"
        />
      );
    }

    return null;
  }

  statusLabel() {
    const { record: { form: { statusField } } } = this.props;
    return statusField?.label ?? '';
  }

  statusValue() {
    const { record: { statusValue } } = this.props;
    return {
      value: statusValue.textValue,
      label: statusValue.displayValue,
    };
  }

  statusChoices() {
    const { record: { form: { statusField } } } = this.props;
    return statusField.choices;
  }

  renderStatusField() {
    const { record, editing, onStatusChange } = this.props;
    const { form: { statusField } } = record;
    const { role } = this.context;

    const enabled = statusField.isEnabled;
    const hidden = statusField.isHidden;

    if (this.isRecord && enabled && !hidden) {
      let readOnly = !statusField.isReadOnly;

      if (!role.canChangeStatus) {
        readOnly = false;
      }

      return (
        <StatusField
          statusLabel={this.statusLabel()}
          statusValue={this.statusValue()}
          statusChoices={this.statusChoices()}
          editing={editing && readOnly}
          field="status"
          onStatusChange={onStatusChange}
        />
      );
    }

    return null;
  }

  renderProjectField() {
    const { record: { form, projectID }, editing, onProjectChange } = this.props;
    const { projectField } = form;
    const { projects, role } = this.context;

    const projectsEnabled = form.isProjectEnabled;
    const hidden = projectsEnabled ? projectField.isHidden : false;
    const disabled = projectsEnabled ? projectField.isDisabled : true;
    const readOnly = !role.canChangeProject || disabled;

    if (this.isRecord && projectsEnabled && !hidden && projects.length) {
      return (
        <ProjectField
          projects={projects}
          projectID={projectID}
          editing={editing && !readOnly}
          field="project"
          onProjectChange={onProjectChange}
        />
      );
    }

    return null;
  }

  renderAssignmentField() {
    const { record: { assignedToID, form }, editing, onAssignmentChange } = this.props;
    const { role, formUsers } = this.context;

    if (this.isRecord && form.isAssignmentEnabled) {
      const enabled = role.canAssignRecords;
      return (
        <AssignmentField
          assignedToID={assignedToID}
          formMembers={formUsers}
          editing={editing && enabled}
          field="assignment"
          onAssignmentChange={onAssignmentChange}
        />
      );
    }

    return null;
  }

  renderChangesetField() {
    if (this.isRecord && this.props.record.changeset) {
      return (
        <ChangesetField
          feature={this.props.feature}
          record={this.props.record}
          editing={this.props.editing}
          field="changeset"
          changeset={this.props.record.changeset}
        />
      );
    }

    return null;
  }

  renderDisplayValue(className, title, value) {
    const { styles } = MetadataView;

    return (
      <div className={styles[className]}>
        <h1>{title}</h1>
        <span>{value}</span>
      </div>
    );
  }

  handleTitleClick = (event) => {
    const expanded = !this.state.expanded;

    this.setState({ expanded });

    this.context.preferences.setSectionExpanded('metadata', expanded);
  };

  get isRecord() {
    return this.props.feature instanceof Record;
  }

  get featureType() {
    return this.isRecord ? 'record' : 'repeatable';
  }
}

const styles = cssInJS({
  container: {
  },

  spacer: {
    height: 7,
    backgroundColor: '#efefef',
    borderBottom: '1px solid #d9d9d9',
  },

  spacerExpanded: {
    height: 7,
    backgroundColor: '#efefef',
    borderTop: '1px solid #d9d9d9',
  },
});

MetadataView.styles = styles;
